import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format, parseISO, addDays, subDays, startOfDay, isSameDay, parse, isAfter, isBefore, isEqual } from 'date-fns';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, TrendingUp, Clock, AlertCircle } from "lucide-react";
import { DateRange } from 'react-day-picker';
import incidentsData from '@/data/incidents.json';

interface PredictiveFeaturesProps {
  dateRange?: DateRange;
}

export function PredictiveFeatures({ dateRange }: PredictiveFeaturesProps) {
  console.log('Initial incidents data:', incidentsData.slice(0, 5));
  
  const parseDate = (dateStr: string | undefined | null): Date | null => {
    if (!dateStr) return null;
    try {
      // Parse date in format "MM/DD/YYYY HH:mm:ss AM/PM"
      return parse(dateStr, 'MM/dd/yyyy hh:mm:ss aa', new Date());
    } catch (error) {
      console.error('Error parsing date:', dateStr, error);
      return null;
    }
  };

  // Set cutoff date to April 17th, 2025
  const cutoffDate = startOfDay(new Date(2025, 3, 17)); // Month is 0-based, so 3 is April
  
  // Get the date range for analysis
  const endDate = dateRange?.to ? startOfDay(dateRange.to) : startOfDay(new Date(2025, 3, 24)); // Show up to April 24th
  const startDate = dateRange?.from ? startOfDay(dateRange.from) : startOfDay(subDays(cutoffDate, 30));

  console.log('Date range:', { startDate, endDate });

  // Generate array of dates for analysis
  const dates = [];
  let currentDate = startDate;
  while (currentDate <= endDate) {
    dates.push(currentDate);
    currentDate = addDays(currentDate, 1);
  }

  console.log('Analysis dates:', dates.map(d => format(d, 'yyyy-MM-dd')));

  // Calculate daily incidents (only for historical data up to April 17th)
  const dailyIncidents = dates
    .filter(date => isBefore(date, cutoffDate) || isEqual(date, cutoffDate))
    .map(date => {
      const incidentsOnDay = incidentsData.filter(incident => {
        const incidentDate = parseDate(incident.Opened);
        return incidentDate && isSameDay(incidentDate, date);
      });
      return {
        x: format(date, "yyyy-MM-dd"),
        y: incidentsOnDay.length
      };
    });

  console.log('Daily incidents:', dailyIncidents);

  // Calculate 7-day moving average
  const calculateMovingAverage = (data: { x: string, y: number }[], windowSize: number = 7) => {
    if (data.length === 0) return [];
    
    return data.map((_, index) => {
      const start = Math.max(0, index - windowSize + 1);
      const window = data.slice(start, index + 1);
      const sum = window.reduce((acc, val) => acc + val.y, 0);
      return {
        x: data[index].x,
        y: Math.round(sum / window.length)
      };
    });
  };

  const movingAverage = calculateMovingAverage(dailyIncidents);
  console.log('Moving average:', movingAverage);

  // Calculate trend using the last 14 days of historical data
  const calculateTrend = (data: { x: string, y: number }[]) => {
    if (data.length < 2) return 0;
    
    const recentData = data.slice(-14);
    const n = recentData.length;
    
    let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
    recentData.forEach((point, i) => {
      sumX += i;
      sumY += point.y;
      sumXY += i * point.y;
      sumXX += i * i;
    });
    
    const slope = ((n * sumXY) - (sumX * sumY)) / ((n * sumXX) - (sumX * sumX));
    return isNaN(slope) ? 0 : slope;
  };

  // Predict from April 18th to April 24th
  const trend = calculateTrend(movingAverage);
  const lastValue = movingAverage.length > 0 ? movingAverage[movingAverage.length - 1].y : 0;
  
  const prediction = dates
    .filter(date => isAfter(date, cutoffDate))
    .map((date, index) => {
      const predictedValue = Math.max(0, Math.round(lastValue + (trend * (index + 1))));
      return {
        x: format(date, "yyyy-MM-dd"),
        y: predictedValue
      };
    });

  // Calculate total predicted incidents
  const totalPredictedIncidents = prediction.reduce((sum, p) => sum + p.y, 0);

  // Calculate max value for graph scaling
  const maxHistorical = movingAverage.length > 0 ? Math.max(...movingAverage.map(d => d.y)) : 0;
  const maxPredicted = prediction.length > 0 ? Math.max(...prediction.map(d => d.y)) : 0;
  const maxValue = Math.max(5, Math.ceil(Math.max(maxHistorical, maxPredicted) * 1.2));

  const incidentProbabilityData = {
    historical: movingAverage,
    prediction,
    maxValue
  };

  console.log('Final data for graph:', incidentProbabilityData);

  // Calculate system capacity impact
  const capacityImpact = {
    avgIncidentsPerDay: Math.round(movingAverage[movingAverage.length - 1]?.y || 0),
    riskLevel: (movingAverage[movingAverage.length - 1]?.y || 0) > 5 ? "High" : 
               (movingAverage[movingAverage.length - 1]?.y || 0) > 2 ? "Medium" : "Low"
  };

  // Calculate alert recommendations
  const alertRecommendations = {
    incidentsPerHour: Math.max(1, Math.ceil((capacityImpact.avgIncidentsPerDay || 0) / 8)),
    deploymentCooldown: Math.max(30, Math.ceil((capacityImpact.avgIncidentsPerDay || 0) * 15)),
    concurrentDeployments: Math.max(1, Math.floor(8 / (capacityImpact.avgIncidentsPerDay || 1)))
  };

  const dateRangeText = useMemo(() => {
    return `Analysis: ${format(startDate, 'MMM d, yyyy')} - ${format(cutoffDate, 'MMM d, yyyy')}\nPrediction: ${format(addDays(cutoffDate, 1), 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
  }, [startDate, cutoffDate, endDate]);

  if (!incidentsData?.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Predictive Features</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            No data available for analysis. Please ensure incidents are loaded.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Predicted Incidents (Next 7 Days)
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.round(incidentProbabilityData.prediction.reduce((sum, p) => sum + p.y, 0))}
            </div>
            <p className="text-xs text-muted-foreground whitespace-pre-line">
              {dateRangeText}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              System Load Risk
            </CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {capacityImpact.riskLevel}
            </div>
            <p className="text-xs text-muted-foreground">
              {capacityImpact.avgIncidentsPerDay.toFixed(1)} incidents/day
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Recommended Cooldown
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {alertRecommendations.deploymentCooldown}min
            </div>
            <p className="text-xs text-muted-foreground">
              Between deployments
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Incident Probability Forecast</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="h-[200px] w-full relative">
            {incidentProbabilityData.historical.length > 0 ? (
              <>
                {/* Y-axis labels */}
                <div className="absolute left-0 h-full flex flex-col justify-between text-xs text-muted-foreground px-2">
                  <span>{Math.round(incidentProbabilityData.maxValue)}</span>
                  <span>{Math.round(incidentProbabilityData.maxValue / 2)}</span>
                  <span>0</span>
                </div>
                
                {/* Graph container */}
                <div className="ml-8 h-full flex items-end space-x-1">
                  {/* Historical data */}
                  {incidentProbabilityData.historical.map((point, i) => (
                    <div
                      key={`hist-${point.x}`}
                      className="flex-1 bg-blue-500/20 hover:bg-blue-500/30 transition-colors relative group"
                      style={{
                        height: `${(point.y / incidentProbabilityData.maxValue) * 100}%`,
                        minHeight: point.y > 0 ? '2px' : '0'
                      }}
                    >
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-popover p-2 rounded shadow-lg text-xs whitespace-nowrap">
                        <strong>{format(parseISO(point.x), 'MMM d, yyyy')}</strong>
                        <br />
                        {point.y.toFixed(1)} incidents
                      </div>
                    </div>
                  ))}

                  {/* Prediction data */}
                  {incidentProbabilityData.prediction.map((point, i) => (
                    <div
                      key={`pred-${point.x}`}
                      className="flex-1 bg-orange-500/20 hover:bg-orange-500/30 transition-colors relative group"
                      style={{
                        height: `${(point.y / incidentProbabilityData.maxValue) * 100}%`,
                        minHeight: point.y > 0 ? '2px' : '0'
                      }}
                    >
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-popover p-2 rounded shadow-lg text-xs whitespace-nowrap">
                        <strong>{format(parseISO(point.x), 'MMM d, yyyy')}</strong>
                        <br />
                        {point.y.toFixed(1)} predicted
                      </div>
                    </div>
                  ))}
                </div>

                {/* Legend */}
                <div className="absolute bottom-0 left-8 right-0 flex justify-center space-x-4 text-xs text-muted-foreground py-2">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500/20 mr-1"></div>
                    Historical
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-orange-500/20 mr-1"></div>
                    Predicted
                  </div>
                </div>
              </>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                No data available for the selected date range
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Alert Threshold Recommendations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Incident Rate Threshold</AlertTitle>
              <AlertDescription>
                Alert if more than {alertRecommendations.incidentsPerHour} incidents occur within an hour
              </AlertDescription>
            </Alert>
            
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Deployment Spacing</AlertTitle>
              <AlertDescription>
                Maintain at least {alertRecommendations.deploymentCooldown} minutes between deployments
              </AlertDescription>
            </Alert>
            
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Concurrent Deployments</AlertTitle>
              <AlertDescription>
                Limit to {alertRecommendations.concurrentDeployments} concurrent deployments
              </AlertDescription>
            </Alert>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 